import React from 'react';
import { motion } from 'framer-motion';
import { CreditCardIcon, FileTextIcon, LeafIcon, LockIcon, MapPinIcon, ShieldCheckIcon, UtensilsIcon, ZapIcon } from 'lucide-react';
import { CtaButton } from '../CtaButton';
import { ColombiaFlag } from '../ColombiaFlag';
import { Highlight } from '../Highlight';
import { heroBadges, heroStats, productImage, trustRow } from '../../data/landing';
const iconMap: Record<string, React.ReactNode> = {
  file: <FileTextIcon className="h-4 w-4 text-brand" data-id="element-149" data-mp="ms7prv4qm7w4" />,
  zap: <ZapIcon className="h-4 w-4 text-brand" data-id="element-150" data-mp="qukm5sxlgtg8" />,
  flag: <MapPinIcon className="h-4 w-4 text-brand" data-id="element-151" data-mp="vmccq3gr2z6d" />,
  leaf: <LeafIcon className="h-4 w-4 text-brand" data-id="element-152" data-mp="vnnexyujryvd" />,
  utensils: <UtensilsIcon className="h-4 w-4 text-brand" data-id="element-153" data-mp="10l5sk2jif29p" />,
  shield: <ShieldCheckIcon className="h-4 w-4 text-brand" data-id="element-154" data-mp="10knleavb73n0" />,
  lock: <LockIcon className="h-4 w-4 text-brand" data-id="element-155" data-mp="15lbc0vfmligp" />,
  card: <CreditCardIcon className="h-4 w-4 text-brand" data-id="element-156" data-mp="15ifev9blqxtb" />
};
export function Hero() {
  return <header className="relative overflow-hidden bg-white px-5 pb-16 pt-12 md:pb-24 md:pt-16" data-id="element-157" data-mp="1ahijisno720r">
      <div className="mx-auto flex max-w-5xl flex-col items-center text-center" data-id="element-158" data-mp="9p443yx51t90">
        <p className="flex items-center gap-3 text-base font-extrabold uppercase tracking-[0.2em] text-brand md:text-lg" data-id="element-159" data-mp="4ny5jkgsrbrk">
          <ColombiaFlag className="h-4 w-6" data-id="element-160" data-mp="3vx3ob0dfrj8m" />
          Hábil Online™
        </p>

        <h1 className="mt-6 text-5xl font-black leading-[1] tracking-tight text-brand sm:text-6xl md:text-8xl" data-id="element-161" data-mp="3rv0ba7b4c6l8">
          Colon Tranquilo
          <span className="mt-2 block" data-id="element-162" data-mp="nf7gzza4oals">
            <Highlight data-id="element-163" data-mp="ng8yalhqltby">en 7 Días.</Highlight>
          </span>
        </h1>

        <motion.div initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.3,
        ease: [0.23, 1, 0.32, 1]
      }} className="mx-auto mt-8 w-full max-w-[380px] sm:max-w-[500px] md:mt-10 md:max-w-[640px]" data-id="element-164" data-mp="inz8d5gmgt9o">
          <img src={productImage} width={1200} height={1000} alt="MegaPack Colon Tranquilo: 5 tomos, 5 bonos y Plan Maestro de 30 días" className="block h-auto w-full object-contain" data-id="element-165" data-mp="16ebov4pxnybv" />
        </motion.div>

        <p className="mt-8 max-w-3xl text-xl font-extrabold leading-snug text-brand md:text-2xl" data-id="element-166" data-mp="35muvsmk494y7">
          El sistema N°1 para personas con colon irritable en Colombia — Días 1 → 30 — Curación
          definitiva
        </p>
        <p className="mt-3 max-w-2xl text-base text-charcoal md:text-lg" data-id="element-167" data-mp="2hvpj2g9h2ss4">
          Menús + recetas + protocolos listos para usar. Sin buscar. Sin improvisar. Sin volver a
          sufrir.
        </p>

        <ul className="mt-6 flex flex-wrap justify-center gap-2" data-id="element-168" data-mp="27xrsvn81ylwv">
          {heroBadges.map(b => <li key={b.label} className="flex items-center gap-2 rounded-full border border-line bg-brand-soft px-4 py-2 text-sm font-semibold text-charcoal" data-id="element-169" data-mp="2cvteofwv4zc1">
              {iconMap[b.icon]}
              {b.label}
            </li>)}
        </ul>

        <div className="mt-8 flex w-full justify-center" data-id="element-170" data-mp="1vmhbwldbpko4">
          <CtaButton href="#bonos" data-id="element-171" data-mp="1vkxwewzhrdne">Quiero mi MegaPack ahora</CtaButton>
        </div>

        <ul className="mt-5 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm font-medium text-muted" data-id="element-172" data-mp="1qpqx01d98v4a">
          {trustRow.map(t => <li key={t.label} className="flex items-center gap-2" data-id="element-173" data-mp="20mdf4ie7582m">
              {iconMap[t.icon]}
              {t.label}
            </li>)}
        </ul>

        <dl className="mt-12 grid w-full max-w-4xl grid-cols-2 overflow-hidden rounded-2xl bg-brand md:grid-cols-4" data-id="element-174" data-mp="2efl8oflu8dmy">
          {heroStats.map((s, i) => <div key={s.label} className={`px-4 py-6 ${i < 2 ? 'border-b border-white/15 md:border-b-0' : ''} ${i % 2 === 0 ? 'border-r border-white/15' : ''} ${i === 1 ? 'md:border-r' : ''}`} data-id="element-175" data-mp="2oamwn84dn9ny" data-mpi={i}>
              <dt className="sr-only" data-id="element-176" data-mp="oytw51idrm6h">{s.label}</dt>
              <dd className="text-3xl font-black text-gold md:text-4xl" data-id="element-177" data-mp="p04z4z404pwj">{s.value}</dd>
              <dd className="mt-1 text-sm font-medium text-white/85" data-id="element-178" data-mp="qexxs5dnck49">{s.label}</dd>
            </div>)}
        </dl>
      </div>
    </header>;
}